export interface FlightSpinLoaderProps {
  size?: "small" | "large";
  className?: string;
}

// Math.cos/Math.sin are implementation-dependent per ECMA-262, so Node and the browser
// can disagree in the last bit and trip a hydration mismatch on the SVG coordinates.
// Rounding is stable across engines and still far finer than the 100-unit viewBox needs.
const roundCoord = (value: number) => Math.round(value * 1000) / 1000;

export const FlightSpinLoader = ({
  size = "small",
  className = "text-brand-blue",
}: FlightSpinLoaderProps) => {
  // explicit pixel dimensions prevent the SVG from expanding before Tailwind styles are loaded.
  const dimension = size === "large" ? 160 : 96;

  let containerSizeClasses = "w-24 h-24"; // default small container size (increased from w-20)

  if (size === "large") {
    containerSizeClasses = "w-40 h-40"; // increased from w-36
  }

  const dotsCount = 28;
  const radius = 40; // increased from 35 to fill the container more
  const center = 50;
  const startAngleDeg = -90; // Top of the circle (12 o'clock)
  const trailLengthDeg = 280; // How far the trail extends around the circle

  const dots = Array.from({ length: dotsCount }).map((_, index) => {
    const angleDeg = startAngleDeg - (index + 1) * (trailLengthDeg / dotsCount);
    const angleRad = (angleDeg * Math.PI) / 180;

    const cx = roundCoord(center + radius * Math.cos(angleRad));
    const cy = roundCoord(center + radius * Math.sin(angleRad));

    const ratio = index / (dotsCount - 1); // 0 to 1
    // Radii: from 2.5px down to 0.5px (scaled up slightly for larger size)
    const r = 2.5 * (1 - ratio * 0.8);
    // Opacity: from 0.9 down to 0.05
    const opacity = 0.9 * (1 - ratio * 0.95);

    return <circle key={index} cx={cx} cy={cy} r={r} fill="currentColor" opacity={opacity} />;
  });

  return (
    <div
      data-testid="loading-spinner"
      className="grid h-full w-full place-content-center"
      style={{
        width: dimension,
        height: dimension,
      }}
    >
      <div className={`relative ${containerSizeClasses} ${className}`}>
        <svg
          viewBox="0 0 100 100"
          width={dimension}
          height={dimension}
          className="w-full h-full animate-spin text-current"
          style={{ animationDuration: "1.2s" }}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* The trail of dots */}
          {dots}

          {/* Plane icon positioned at the head of the trail (12 o'clock) and oriented tangent to the circle */}
          <g
            transform={`translate(${center}, ${center - radius}) rotate(90) scale(0.75) translate(-12, -12)`}
          >
            <path
              d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V21l-2 1v1h6v-1l-2-1v-7.5l8 2.5z"
              fill="currentColor"
            />
          </g>
        </svg>
      </div>
    </div>
  );
};

export default FlightSpinLoader;
